/**
 * Tick generation for one band (features.md §2 and §6bis).
 *
 * Four rules, in decreasing priority for labelling:
 *  - both band bounds are always drawn and labelled, even off the regular
 *    rhythm — a band ending at 1495 must say 1495, not stop at 1475;
 *  - majors, every 5×step, carry the regular scale and are drawn bold, unit
 *    included;
 *  - an anchor year — the start or end of a period — forces a label on the two
 *    graduations that bracket it, so the bar can be placed without ever showing
 *    an off-grid date. Not bold, and without the unit.
 *  - every other regular tick is drawn unlabelled.
 */

import { formatYear, formatYearValue } from '../model/epoch';
import type { Band } from '../model/types';
import type { Scale } from './scale';

/** Minimum gap between two labels on the same row. */
const LABEL_GAP_PX = 4;

/** Rough width of a 9px label; injectable so layout stays testable without a DOM. */
export const approxLabelWidth = (text: string): number => text.length * 5.5;

export interface Tick {
  year: number;
  x: number;
  /**
   * Where the label is centred, which is not always the tick: a label centred on
   * the band's first graduation hangs half its width off the left edge and gets
   * clipped by the page. Edge labels are pulled back inside the band instead.
   */
  labelX: number;
  /** Bold line: majors and band bounds. */
  major: boolean;
  label: string | null;
  /** Bound and major labels are bold; forced ones are not. */
  labelBold: boolean;
}

type Priority = 'bound' | 'major' | 'forced';

function regularYears(band: Band): number[] {
  const years: number[] = [];
  for (let year = band.start; year < band.end; year += band.step) years.push(year);
  // The end bound is always drawn, whether or not the step lands on it.
  years.push(band.end);
  return years;
}

/**
 * Pulls a centred label back inside the band. The label stops sitting exactly
 * over its graduation at the two ends, which is the usual trade on an axis: a
 * label a few pixels off its tick still reads, a clipped one does not.
 */
function keepInside(x: number, half: number, width: number): number {
  if (width < half * 2) return width / 2;
  return Math.min(Math.max(x, half), width - half);
}

/** The regular tick immediately at or below `year`. */
function tickBelow(band: Band, year: number): number {
  const steps = Math.floor((year - band.start) / band.step);
  return band.start + steps * band.step;
}

function labelCandidates(band: Band, anchorYears: readonly number[]): Map<number, Priority> {
  const candidates = new Map<number, Priority>();
  const majorEvery = band.step * 5;
  const drawn = new Set(regularYears(band));

  for (const year of regularYears(band)) {
    if ((year - band.start) % majorEvery === 0) candidates.set(year, 'major');
  }

  // A dated thing is bracketed by its two neighbouring graduations, both
  // labelled: a start in 1642 at a 5-year step shows 1640 *and* 1645, so the
  // reader can place it without ever seeing an off-grid date.
  // Forced labels never override a major, which is already a reference point.
  for (const year of anchorYears) {
    if (year < band.start || year > band.end) continue;

    const below = tickBelow(band, year);
    if (!candidates.has(below)) candidates.set(below, 'forced');
    if (below === year) continue;

    const above = Math.min(below + band.step, band.end);
    if (drawn.has(above) && !candidates.has(above)) candidates.set(above, 'forced');
  }

  // Bounds win outright — they say where the band actually stops.
  candidates.set(band.start, 'bound');
  candidates.set(band.end, 'bound');

  return candidates;
}

/**
 * Keeps the axis to a single line of labels, dropping whatever does not fit.
 *
 * Labels were briefly stacked on extra rows so that every period bound kept both
 * of its bracketing graduations. That guarantee turned out to cost more than it
 * was worth: on a 5-year band at print scale, consecutive labels are 27 px apart
 * and 22 px wide, so almost every forced label ended up on a second line and the
 * axis grew for all of them. A missing intermediate graduation is a minor loss;
 * a taller axis on every band is not.
 *
 * Priority decides who survives a collision: a band bound is always drawn
 * (features.md §2), a major carries the regular scale, a forced label is a
 * convenience. Placement is by priority rather than left to right, so the
 * occupied spans are tracked as intervals — the start bound is placed after
 * every major, at x = 0.
 */
function assignLabels(
  ticks: Tick[],
  priorities: Map<number, Priority>,
  measure: (text: string) => number,
): void {
  const taken: Array<[number, number]> = [];
  const labelled = ticks.filter((tick) => tick.label !== null);

  const order: Priority[] = ['bound', 'major', 'forced'];
  for (const priority of order) {
    for (const tick of labelled) {
      if (priorities.get(tick.year) !== priority) continue;

      const half = measure(tick.label ?? '') / 2;
      const span: [number, number] = [tick.labelX - half, tick.labelX + half];

      const free = taken.every(
        ([from, to]) => span[1] + LABEL_GAP_PX <= from || span[0] >= to + LABEL_GAP_PX,
      );

      if (free) taken.push(span);
      else tick.label = null;
    }
  }
}

export function buildTicks(
  band: Band,
  scale: Scale,
  /** Years that must be bracketed by two labelled graduations. */
  anchorYears: readonly number[] = [],
  measure: (text: string) => number = approxLabelWidth,
): Tick[] {
  const priorities = labelCandidates(band, anchorYears);

  const ticks: Tick[] = regularYears(band).map((year) => {
    const priority = priorities.get(year);
    const isBound = priority === 'bound';
    const isMajor = priority === 'major' || isBound;

    // The unit rides on the bold labels only. Repeating "Ma AP" on every
    // intermediate graduation triples their width for nothing: the scale is
    // already stated by the majors around them.
    const label = priority
      ? isMajor
        ? formatYear(year, band.unit)
        : formatYearValue(year, band.unit)
      : null;

    const x = scale.x(year);

    return {
      year,
      x,
      labelX: label === null ? x : keepInside(x, measure(label) / 2, scale.width),
      major: isMajor,
      label,
      labelBold: isMajor,
    };
  });

  assignLabels(ticks, priorities, measure);
  return ticks;
}
